import { useState, useEffect } from "react";
import {
  CheckCircle2, XCircle, Clock, ChevronLeft, ChevronRight,
  Eye, Plus, Receipt, Filter, X, Wallet, Building2, AlertCircle,
  Printer, Zap, Wifi, Server, Cpu, Truck, ArrowLeftRight, User,
  Store, ChevronDown, Link2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { expensesApi, partiesApi } from "@/lib/unifiedApi";
import { approveExpense, rejectExpense } from "@/lib/attendance";
import { sendNotification } from "@/lib/messaging";
import { useAuth } from "@/context/AuthContext";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from "date-fns";
import { th } from "date-fns/locale";

// ─── Constants ────────────────────────────────────────────
const CATEGORIES = [
  { value: "Refund",          Icon: ArrowLeftRight, color: "#ef4444" },
  { value: "Print",           Icon: Printer,        color: "#f59e0b" },
  { value: "ค่าใช้จ่ายทั่วไป", Icon: Receipt,        color: "#6366f1" },
  { value: "ค่าน้ำค่าไฟ",      Icon: Zap,            color: "#0ea5e9" },
  { value: "ค่าอินเตอร์เน็ต",  Icon: Wifi,           color: "#10b981" },
  { value: "โครงตู้",          Icon: Server,         color: "#8b5cf6" },
  { value: "Electronic",      Icon: Cpu,            color: "#ec4899" },
  { value: "ขนส่ง",            Icon: Truck,          color: "#f97316" },
];

const catMap = Object.fromEntries(CATEGORIES.map((c) => [c.value, c]));

const EMPTY_FORM = {
  category: "", amount: "", slip_date: format(new Date(), "yyyy-MM-dd"),
  merchant: "", payer: "", payee: "", bank_from: "", bank_to: "",
  ref_no: "", payment_type: "company", note: "",
  party_id: null, party_name: "",
};

const fmt = (n) => Number(n || 0).toLocaleString("th-TH", { minimumFractionDigits: 2 });

// ─── Status badge ──────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    pending:   { label: "รอยืนยัน",  color: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30" },
    confirmed: { label: "ยืนยันแล้ว", color: "bg-green-500/20 text-green-300 border-green-500/30" },
    rejected:  { label: "ปฏิเสธ",    color: "bg-red-500/20 text-red-300 border-red-500/30" },
  };
  const s = map[status] ?? map.pending;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${s.color}`}>
      {s.label}
    </span>
  );
}

// ─── Payment type badge ────────────────────────────────────
function PayTypeBadge({ type }) {
  return type === "advance"
    ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30 flex items-center gap-1 w-fit whitespace-nowrap"><Wallet size={10}/>สำรองจ่าย</span>
    : <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30 flex items-center gap-1 w-fit whitespace-nowrap"><Building2 size={10}/>บริษัทจ่าย</span>;
}

// ─── Add/Edit Dialog ───────────────────────────────────────
function ExpenseDialog({ open, onClose, expense, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorMenu, setVendorMenu] = useState(false);
  const [vendorQ, setVendorQ] = useState("");
  const { user } = useAuth();

  useEffect(() => {
    setForm(expense
      ? { category: expense.category ?? "", amount: String(expense.amount ?? ""), slip_date: expense.slip_date ?? format(new Date(), "yyyy-MM-dd"), merchant: expense.merchant ?? "", payer: expense.payer ?? "", payee: expense.payee ?? "", bank_from: expense.bank_from ?? "", bank_to: expense.bank_to ?? "", ref_no: expense.ref_no ?? "", payment_type: expense.payment_type ?? "company", note: expense.note ?? "", party_id: expense.party_id ?? null, party_name: expense.party_name ?? "" }
      : EMPTY_FORM
    );
  }, [expense, open]);

  // Vendor register for the picker (vendors only, active). Silently empty for
  // non-finance users (rules deny bizops_parties to them).
  useEffect(() => {
    if (!open) return undefined;
    const unsub = partiesApi.subscribe((data) => {
      setVendors(
        data
          .filter((p) => p.active !== false && (p.types || []).includes("vendor"))
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", "th"))
      );
    }, () => setVendors([]));
    return unsub;
  }, [open]);

  const selectVendor = (p) => {
    setForm((prev) => ({ ...prev, party_id: p.id, party_name: p.name,
      payee: prev.payee || p.name, merchant: prev.merchant || p.name }));
    setVendorMenu(false); setVendorQ("");
  };
  const clearVendor = () => setForm((prev) => ({ ...prev, party_id: null, party_name: "" }));

  async function save() {
    if (!form.amount || isNaN(Number(form.amount))) { toast.error("กรุณากรอกยอดเงิน"); return; }
    setSaving(true);
    try {
      const data = { ...form, amount: parseFloat(form.amount), submitted_by: user?.name ?? "unknown" };
      if (expense?.id) {
        await expensesApi.update(expense.id, data);
        toast.success("อัปเดตสำเร็จ");
      } else {
        await expensesApi.create({ ...data, status: "confirmed" });
        toast.success("เพิ่มรายการสำเร็จ");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error("เกิดข้อผิดพลาด: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  const f = (k) => (v) => setForm((prev) => ({ ...prev, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{expense ? "แก้ไขรายการ" : "เพิ่มรายการค่าใช้จ่าย"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Vendor (party) picker — for AP */}
          <div className="relative">
            <Label className="text-gray-400 text-xs mb-1 block">ผู้ขาย (จากทะเบียนคู่ค้า)</Label>
            {form.party_id ? (
              <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-md px-3 py-2">
                <span className="text-sm text-white flex items-center gap-1.5">
                  <Link2 size={13} className="text-[#BFFF00]" />
                  {vendors.find((v) => v.id === form.party_id)?.name || form.party_name || "ผูกแล้ว"}
                </span>
                <button type="button" onClick={clearVendor} className="text-xs text-gray-400 hover:text-red-400">ยกเลิกผูก</button>
              </div>
            ) : (
              <>
                <button type="button" onClick={() => setVendorMenu((v) => !v)}
                  className="w-full flex items-center justify-between bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-gray-400 hover:border-white/20">
                  <span className="flex items-center gap-1.5"><Store size={13} />เลือกผู้ขาย… (ไม่บังคับ)</span>
                  <ChevronDown size={15} />
                </button>
                {vendorMenu && (
                  <div className="absolute z-30 mt-1 w-full rounded-lg bg-[#222] border border-white/15 shadow-xl max-h-52 overflow-hidden flex flex-col">
                    <div className="p-2 border-b border-white/10">
                      <input autoFocus value={vendorQ} onChange={(e) => setVendorQ(e.target.value)}
                        placeholder="ค้นหาชื่อ / เลขภาษี"
                        className="w-full bg-black/40 border border-white/10 rounded text-white text-sm p-1.5 outline-none" />
                    </div>
                    <div className="overflow-y-auto">
                      {vendors
                        .filter((p) => {
                          const q = vendorQ.trim().toLowerCase();
                          if (!q) return true;
                          return (p.name || "").toLowerCase().includes(q) || (p.tax_id || "").includes(q);
                        })
                        .map((p) => (
                          <button key={p.id} type="button" onClick={() => selectVendor(p)}
                            className="w-full text-left px-3 py-2 hover:bg-white/10 text-sm">
                            <div className="text-white">{p.name}</div>
                            <div className="text-gray-500 text-xs">{p.code}{p.tax_id ? ` · ${p.tax_id}` : ""}</div>
                          </button>
                        ))}
                      {vendors.length === 0 && (
                        <div className="px-3 py-4 text-center text-gray-500 text-sm">ยังไม่มีผู้ขายในทะเบียน</div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Category + Payment type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-400 text-xs mb-1 block">หมวดหมู่</Label>
              <Select value={form.category} onValueChange={f("category")}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="เลือกหมวด" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}><c.Icon size={13} style={{display:'inline',verticalAlign:'middle',marginRight:5}}/>{c.value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-400 text-xs mb-1 block">ประเภทการชำระ</Label>
              <Select value={form.payment_type} onValueChange={f("payment_type")}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                  <SelectItem value="company"><Building2 size={13} style={{display:'inline',verticalAlign:'middle',marginRight:5}}/>บริษัทจ่าย</SelectItem>
                  <SelectItem value="advance"><User size={13} style={{display:'inline',verticalAlign:'middle',marginRight:5}}/>สำรองจ่าย</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Amount + Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-400 text-xs mb-1 block">ยอดเงิน (฿) *</Label>
              <Input value={form.amount} onChange={(e) => f("amount")(e.target.value)} placeholder="0.00" type="number" step="0.01" className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs mb-1 block">วันที่</Label>
              <Input value={form.slip_date} onChange={(e) => f("slip_date")(e.target.value)} type="date" className="bg-white/5 border-white/10 text-white" />
            </div>
          </div>

          {/* Merchant */}
          <div>
            <Label className="text-gray-400 text-xs mb-1 block">ร้านค้า / ชื่อผู้รับเงิน</Label>
            <Input value={form.merchant} onChange={(e) => f("merchant")(e.target.value)} placeholder="ชื่อร้านค้า หรือผู้รับ" className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" />
          </div>

          {/* Payer / Payee */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-400 text-xs mb-1 block">ผู้โอน</Label>
              <Input value={form.payer} onChange={(e) => f("payer")(e.target.value)} placeholder="ชื่อผู้โอน" className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs mb-1 block">ผู้รับ</Label>
              <Input value={form.payee} onChange={(e) => f("payee")(e.target.value)} placeholder="ชื่อผู้รับ" className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" />
            </div>
          </div>

          {/* Bank */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-400 text-xs mb-1 block">ธนาคารต้นทาง</Label>
              <Input value={form.bank_from} onChange={(e) => f("bank_from")(e.target.value)} placeholder="กสิกร / SCB ..." className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs mb-1 block">ธนาคารปลายทาง</Label>
              <Input value={form.bank_to} onChange={(e) => f("bank_to")(e.target.value)} placeholder="กรุงไทย / BBL ..." className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" />
            </div>
          </div>

          {/* Ref + Note */}
          <div>
            <Label className="text-gray-400 text-xs mb-1 block">เลขอ้างอิง</Label>
            <Input value={form.ref_no} onChange={(e) => f("ref_no")(e.target.value)} placeholder="Ref. No." className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" />
          </div>
          <div>
            <Label className="text-gray-400 text-xs mb-1 block">หมายเหตุ</Label>
            <Input value={form.note} onChange={(e) => f("note")(e.target.value)} placeholder="หมายเหตุ (ถ้ามี)" className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" />
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={onClose} variant="outline" className="flex-1 border-white/10 text-white bg-transparent hover:bg-white/5">ยกเลิก</Button>
            <Button onClick={save} disabled={saving} className="flex-1 bg-[#BFFF00] text-black hover:bg-[#a8e000] font-semibold">
              {saving ? "กำลังบันทึก..." : expense ? "อัปเดต" : "บันทึก"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Detail Modal ──────────────────────────────────────────
function ExpenseDetailModal({ expense, open, onClose, onAction }) {
  const { isAdmin } = useAuth();
  const [acting, setActing] = useState(false);

  async function doAction(action) {
    setActing(true);
    try {
      if (action === "confirm") await expensesApi.confirm(expense.id);
      else await expensesApi.reject(expense.id);
      toast.success(action === "confirm" ? "ยืนยันแล้ว" : "ปฏิเสธแล้ว");
      onAction();
      onClose();
    } catch (e) {
      toast.error("เกิดข้อผิดพลาด");
    } finally {
      setActing(false);
    }
  }

  if (!expense) return null;
  const cat = catMap[expense.category];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt size={18} className="text-[#BFFF00]" />
            รายละเอียดค่าใช้จ่าย
          </DialogTitle>
        </DialogHeader>

        {/* Slip image */}
        {expense.slip_image_url && (
          <div className="rounded-xl overflow-hidden border border-white/10 mb-2">
            <img src={expense.slip_image_url} alt="สลิป" className="w-full object-contain max-h-64" />
          </div>
        )}

        {/* Main info */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-3xl font-bold text-[#BFFF00]">฿{fmt(expense.amount)}</span>
            <div className="flex flex-col items-end gap-1">
              <StatusBadge status={expense.status} />
              <PayTypeBadge type={expense.payment_type} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm">
            {[
              ["หมวดหมู่", expense.category ?? "-"],
              ["วันที่", expense.slip_date ?? "-"],
              ["ร้านค้า/ผู้รับเงิน", expense.merchant ?? "-"],
              ["เลขอ้างอิง", expense.ref_no ?? "-"],
              ["ผู้โอน", expense.payer ?? "-"],
              ["ผู้รับ", expense.payee ?? "-"],
              ["ธนาคารต้นทาง", expense.bank_from ?? "-"],
              ["ธนาคารปลายทาง", expense.bank_to ?? "-"],
              ["ส่งโดย", expense.submitted_by ?? "-"],
              ["หมายเหตุ", expense.note || "-"],
            ].map(([k, v]) => (
              <div key={k} className="bg-white/5 rounded-lg p-2">
                <p className="text-gray-400 text-xs">{k}</p>
                <p className="text-white text-sm font-medium mt-0.5 truncate" title={String(v)}>{v}</p>
              </div>
            ))}
          </div>

          {/* Admin actions for pending */}
          {isAdmin() && expense.status === "pending" && (
            <div className="flex gap-2 pt-2">
              <Button onClick={() => doAction("reject")} disabled={acting} variant="outline" className="flex-1 border-red-500/30 text-red-400 bg-transparent hover:bg-red-500/10">
                <XCircle size={14} className="mr-1" /> ปฏิเสธ
              </Button>
              <Button onClick={() => doAction("confirm")} disabled={acting} className="flex-1 bg-[#BFFF00] text-black hover:bg-[#a8e000] font-semibold">
                <CheckCircle2 size={14} className="mr-1" /> ยืนยัน
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ─────────────────────────────────────────────
export default function ExpensesPage() {
  const { isAdmin, user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [filterCat, setFilterCat] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPay, setFilterPay] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [detailExpense, setDetailExpense] = useState(null);
  const [tableActing, setTableActing] = useState({});

  // Real-time: company expenses (bizops_expenses)
  useEffect(() => {
    setLoading(true);
    const unsub = expensesApi.subscribeAll((data) => {
      setExpenses(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Real-time: staff expense claims (expense_claims collection)
  const [claims, setClaims] = useState([]);
  const [claimsActing, setClaimsActing] = useState({});
  useEffect(() => {
    // user is null on first render (async auth load) — wait until user is known
    if (!user || user.role !== 'admin') return;
    // No orderBy: old docs without createdAt would be silently dropped by Firestore.
    // Sort client-side instead so every document is returned.
    const q = query(collection(db, "expense_claims"));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.slip_date || '').localeCompare(a.slip_date || ''));
      setClaims(data);
    });
    return () => unsub();
  }, [user]);

  const pendingClaims = claims.filter((c) => c.status === "pending");

  async function handleClaimAction(claimId, action) {
    setClaimsActing((prev) => ({ ...prev, [claimId]: true }));
    const claim = claims.find((c) => c.id === claimId);
    try {
      if (action === "approve") {
        await approveExpense(claimId, user?.name || 'Admin');
        toast.success("อนุมัติแล้ว");
        if (claim?.userId) sendNotification({ type: 'expense_approved', title: 'คำขอเบิกเงินได้รับการอนุมัติ', body: `${user?.name || 'Admin'} อนุมัติคำขอเบิก ${claim.category || ''} ฿${Number(claim.amount || 0).toLocaleString()} ของคุณแล้ว`, targetUserIds: [claim.userId], data: { url: '/profile' } });
      } else {
        await rejectExpense(claimId, user?.name || 'Admin');
        toast.error("ปฏิเสธแล้ว");
        if (claim?.userId) sendNotification({ type: 'expense_rejected', title: 'คำขอเบิกเงินไม่ผ่านการอนุมัติ', body: `${user?.name || 'Admin'} ไม่อนุมัติคำขอเบิก ${claim.category || ''} ของคุณ`, targetUserIds: [claim.userId], data: { url: '/profile' } });
      }
    } catch (e) {
      toast.error("เกิดข้อผิดพลาด: " + e.message);
    } finally {
      setClaimsActing((prev) => ({ ...prev, [claimId]: false }));
    }
  }

  // Filter by month + category + status + payment_type
  const monthStr = format(currentMonth, "yyyy-MM");
  const filtered = expenses.filter((e) => {
    const inMonth = e.slip_date ? e.slip_date.startsWith(monthStr) : (e.date ? e.date.startsWith(monthStr) : false);
    const inCat = filterCat === "all" || e.category === filterCat;
    const inStatus = filterStatus === "all" || e.status === filterStatus;
    const inPay = filterPay === "all" || e.payment_type === filterPay;
    return inMonth && inCat && inStatus && inPay;
  });

  // Summary stats from filtered
  const confirmed = filtered.filter((e) => e.status === "confirmed");
  const stats = {
    total:   confirmed.reduce((s, e) => s + Number(e.amount ?? 0), 0),
    pending: filtered.filter((e) => e.status === "pending").length,
    advance: confirmed.filter((e) => e.payment_type === "advance").reduce((s, e) => s + Number(e.amount ?? 0), 0),
    company: confirmed.filter((e) => e.payment_type === "company").reduce((s, e) => s + Number(e.amount ?? 0), 0),
  };

  function reload() {}  // real-time already handles it

  return (
    <div className="pt-16 lg:pt-6 px-4 sm:px-6 pb-6 space-y-6 min-h-screen" style={{ backgroundColor: "#0a0a0a", color: "#fff" }}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Receipt size={22} className="text-[#BFFF00]" />
            ค่าใช้จ่าย
          </h1>
          <p className="text-gray-400 text-sm mt-0.5">บันทึกและติดตามค่าใช้จ่ายของบริษัท</p>
        </div>
        {isAdmin() && (
          <Button onClick={() => setAddOpen(true)} className="bg-[#BFFF00] text-black hover:bg-[#a8e000] font-semibold rounded-full">
            <Plus size={16} className="sm:mr-1.5" />
            <span className="hidden sm:inline">เพิ่มรายการ</span>
          </Button>
        )}
      </div>

      {/* Staff expense claims pending approval — admin only */}
      {isAdmin() && pendingClaims.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-yellow-500/20">
            <Clock size={15} className="text-yellow-400" />
            <span className="text-yellow-300 font-semibold text-sm">รอการอนุมัติ — การเบิกจ่ายพนักงาน</span>
            <span className="ml-auto bg-yellow-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">{pendingClaims.length}</span>
          </div>
          <div className="divide-y divide-yellow-500/10">
            {pendingClaims.map((c) => (
              <div key={c.id} className="flex items-center gap-3 px-4 py-3">
                <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-300 text-xs font-bold flex-shrink-0">
                  {c.userName?.[0]?.toUpperCase() ?? "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white text-sm font-medium">{c.userName}</span>
                    <span className="text-gray-400 text-xs">{c.category ?? "-"}</span>
                    {c.date && <span className="text-gray-500 text-xs">{c.date}</span>}
                  </div>
                  {c.description && <p className="text-gray-400 text-xs truncate mt-0.5">{c.description}</p>}
                </div>
                <div className="text-white font-bold text-sm flex-shrink-0 mr-2">฿{Number(c.amount || 0).toLocaleString()}</div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button
                    disabled={claimsActing[c.id]}
                    onClick={() => handleClaimAction(c.id, "reject")}
                    className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors disabled:opacity-40"
                    title="ปฏิเสธ"
                  ><XCircle size={16} /></button>
                  <button
                    disabled={claimsActing[c.id]}
                    onClick={() => handleClaimAction(c.id, "approve")}
                    className="p-1.5 rounded-lg bg-green-500/10 hover:bg-green-500/20 text-green-400 transition-colors disabled:opacity-40"
                    title="อนุมัติ"
                  ><CheckCircle2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Month navigator */}
      <div className="flex items-center gap-3">
        <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
          <ChevronLeft size={16} className="text-gray-400" />
        </button>
        <span className="text-white font-semibold min-w-[120px] text-center">
          {format(currentMonth, "MMMM yyyy", { locale: th })}
        </span>
        <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
          <ChevronRight size={16} className="text-gray-400" />
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: "ยืนยันแล้ว",  value: `฿${fmt(stats.total)}`,          icon: <Receipt size={16}/>, accent: "#BFFF00" },
          { label: "รอยืนยัน",    value: stats.pending + " รายการ",        icon: <Clock size={16}/>,   accent: "#f59e0b" },
          { label: "สำรองจ่าย",   value: `฿${fmt(stats.advance)}`,         icon: <Wallet size={16}/>,  accent: "#a78bfa" },
          { label: "บริษัทจ่าย",  value: `฿${fmt(stats.company)}`,         icon: <Building2 size={16}/>, accent: "#60a5fa" },
        ].map((s) => (
          <div key={s.label} className="bg-[#252525] rounded-2xl p-4 border border-white/5">
            <div className="flex items-center gap-1.5 mb-2" style={{ color: s.accent }}>
              {s.icon}
              <span className="text-xs font-medium text-gray-400 whitespace-nowrap">{s.label}</span>
            </div>
            <p className="text-lg font-bold text-white">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter bar — horizontal scroll on mobile */}
      <div className="flex gap-2 items-center overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
        <Filter size={14} className="text-gray-400 shrink-0" />
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="shrink-0 w-36 bg-white/5 border-white/10 text-white text-sm">
            <SelectValue placeholder="ทุกหมวด" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
            <SelectItem value="all">ทุกหมวด</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}><c.Icon size={13} style={{display:'inline',verticalAlign:'middle',marginRight:5}}/>{c.value}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="shrink-0 w-32 bg-white/5 border-white/10 text-white text-sm">
            <SelectValue placeholder="ทุกสถานะ" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
            <SelectItem value="all">ทุกสถานะ</SelectItem>
            <SelectItem value="pending">รอยืนยัน</SelectItem>
            <SelectItem value="confirmed">ยืนยันแล้ว</SelectItem>
            <SelectItem value="rejected">ปฏิเสธ</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterPay} onValueChange={setFilterPay}>
          <SelectTrigger className="shrink-0 w-32 bg-white/5 border-white/10 text-white text-sm">
            <SelectValue placeholder="ทุกประเภท" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
            <SelectItem value="all">ทุกประเภท</SelectItem>
            <SelectItem value="company">บริษัทจ่าย</SelectItem>
            <SelectItem value="advance">สำรองจ่าย</SelectItem>
          </SelectContent>
        </Select>
        {(filterCat !== "all" || filterStatus !== "all" || filterPay !== "all") && (
          <button onClick={() => { setFilterCat("all"); setFilterStatus("all"); setFilterPay("all"); }}
            className="shrink-0 flex items-center gap-1 text-xs text-gray-400 hover:text-white whitespace-nowrap">
            <X size={12} /> ล้างตัวกรอง
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-[#1a1a1a] rounded-2xl border border-white/5 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-[#BFFF00] mr-3" />
            กำลังโหลด...
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <AlertCircle size={32} className="mb-3 opacity-40" />
            <p>ไม่มีรายการในเดือนนี้</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-sm">
              <thead>
                <tr className="border-b border-white/5">
                  {["วันที่", "หมวด", "ร้านค้า/ผู้รับ", "ยอดเงิน", "ประเภท", "ผู้ส่ง", "สถานะ", ""].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const cat = catMap[e.category];
                  return (
                    <tr key={e.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{e.slip_date ?? "-"}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {cat
                          ? <span className="flex items-center gap-1.5 text-gray-200"><cat.Icon size={13} style={{color:cat.color}}/><span>{e.category}</span></span>
                          : <span className="text-gray-500">{e.category ?? "-"}</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-gray-300 max-w-[160px] truncate">{e.merchant ?? "-"}</td>
                      <td className="px-4 py-3 font-semibold text-white whitespace-nowrap">฿{fmt(e.amount)}</td>
                      <td className="px-4 py-3"><PayTypeBadge type={e.payment_type} /></td>
                      <td className="px-4 py-3 text-gray-400">{e.submitted_by ?? "-"}</td>
                      <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => setDetailExpense(e)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors" title="ดูรายละเอียด">
                            <Eye size={14} />
                          </button>
                          {isAdmin() && e.status === "pending" && (
                            <>
                              <button
                                disabled={tableActing[e.id]}
                                onClick={async () => {
                                  if (tableActing[e.id]) return;
                                  setTableActing((p) => ({ ...p, [e.id]: true }));
                                  try { await expensesApi.confirm(e.id); toast.success("ยืนยันแล้ว"); }
                                  catch { toast.error("เกิดข้อผิดพลาด"); }
                                  finally { setTableActing((p) => ({ ...p, [e.id]: false })); }
                                }}
                                className="p-1.5 rounded-lg bg-green-500/10 hover:bg-green-500/20 text-green-400 transition-colors disabled:opacity-40"
                                title="ยืนยัน"
                              ><CheckCircle2 size={14} /></button>
                              <button
                                disabled={tableActing[e.id]}
                                onClick={async () => {
                                  if (tableActing[e.id]) return;
                                  setTableActing((p) => ({ ...p, [e.id]: true }));
                                  try { await expensesApi.reject(e.id); toast.error("ปฏิเสธแล้ว"); }
                                  catch { toast.error("เกิดข้อผิดพลาด"); }
                                  finally { setTableActing((p) => ({ ...p, [e.id]: false })); }
                                }}
                                className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors disabled:opacity-40"
                                title="ปฏิเสธ"
                              ><XCircle size={14} /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {/* Footer count */}
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-white/5 text-xs text-gray-500">
            {filtered.length} รายการ · ยืนยันแล้ว {confirmed.length} รายการ
          </div>
        )}
      </div>

      {/* Dialogs */}
      <ExpenseDialog open={addOpen} onClose={() => setAddOpen(false)} expense={null} onSaved={reload} />
      <ExpenseDialog open={!!editExpense} onClose={() => setEditExpense(null)} expense={editExpense} onSaved={reload} />
      <ExpenseDetailModal open={!!detailExpense} onClose={() => setDetailExpense(null)} expense={detailExpense} onAction={reload} />
    </div>
  );
}
